import className from "classnames/bind";
import { useEffect, useRef } from "react";
import { useRouter } from "next/router";
import QuizControls from "./QuizControls";
import QuizProgress from "./QuizProgress";
import { shareResult, buildModuleUrl, buildResultShareText } from "./quizShare";
import { usePageWrapperData } from "@/providers/PageWrapperContext";

import styles from "./DigitalExhibitQuiz.module.scss";
const cx = className.bind(styles);

// Structural, so both a scored tier and a personality result satisfy it.
type QuizResultsCopy = {
  eyebrow?: string | null;
  headline?: string | null;
  message?: string | null;
  shareText?: string | null;
};

type QuizResultsProps = {
  title?: string | null;
  score: number;
  total: number;
  result: QuizResultsCopy | null;
  isPersonality?: boolean;
  showShare?: boolean;
  onRetake: () => void;
};

export default function QuizResults({ title, score, total, result, isPersonality, showShare, onRetake }: QuizResultsProps) {
  const router = useRouter();
  const { labels } = usePageWrapperData();

  // Resolve true only when the link was copied so the ShareButton shows its
  // "copied" state; a native share sheet (mobile) reports "shared" and stays default.
  const handleShare = async (): Promise<boolean> => {
    const url = buildModuleUrl(window.location.origin, router.asPath, "quiz");
    const text = buildResultShareText({
      title,
      score: isPersonality ? undefined : score,
      total: isPersonality ? undefined : total,
      shareText: result?.shareText,
      url,
    });
    return (await shareResult({ text })) === "copied";
  };

  // Focus lands on the results copy when it mounts — the final Next button
  // unmounts under the keyboard user, and the screen reader should read the
  // score/tier once before Tab reaches Retake/Share. The copy block is the
  // aria-live region, but live announcements are polite (queued), so the
  // focus announcement and the region never talk over each other.
  const copyRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    copyRef.current?.focus({ preventScroll: true });
  }, []);

  return (
    <div className={cx("dx-quiz__results")}>
      <QuizProgress label={`${total} / ${total}`} pct={100} />

      <div ref={copyRef} tabIndex={-1} className={cx("dx-quiz__results-copy")} data-quiz-stagger aria-live="polite">
        {result?.eyebrow && <span className={cx("dx-quiz__eyebrow", "dx-caption")}>{result.eyebrow}</span>}
        {!isPersonality && (
          <p className={cx("dx-quiz__score", "dx-subhead")}>
            {score} / {total}
          </p>
        )}
        {result && (
          <>
            {result.headline && (
              <h2 className={cx("dx-quiz__results-headline", "dx-display-1")}>
                {result.headline}
              </h2>
            )}
            {result.message && (
              <p className={cx("dx-quiz__results-message", "dx-body")}>{result.message}</p>
            )}
          </>
        )}
      </div>

      <QuizControls
        variant="results"
        onRetake={onRetake}
        onShare={handleShare}
        showShare={showShare}
        retakeLabel={labels?.quizRetake || undefined}
        shareLabel={labels?.quizShareText || undefined}
        copiedLabel={labels?.quizCopiedText || undefined}
      />
    </div>
  );
}
